import { Navigate } from 'react-router';
import type { PortalModule } from '@/types/investor';
import { usePortalAccess } from '@/portal/shared/hooks/usePortalAccess';
import React from 'react';

interface RequireModuleProps {
  module: PortalModule;
  children: React.ReactNode;
  /** Fallback path if access denied. If not provided, redirects to first accessible module */
  fallback?: string;
}

// Module to route mapping for redirect fallback
const MODULE_ROUTES: Record<PortalModule, string> = {
  dashboard: '/portal',
  holdings: '/portal/holdings',
  transactions: '/portal/transactions',
  documents: '/portal/documents',
  profile: '/portal/profile',
  fund_carry: '/portal/carry',
  change_data: '/portal/change-data',
};

// Priority order for fallback redirect
const MODULE_PRIORITY: PortalModule[] = [
  'dashboard',
  'holdings',
  'transactions',
  'documents',
  'fund_carry',
  'profile',
  'change_data',
];

/**
 * Route guard component that checks if user has access to a specific module.
 * Redirects to the first accessible module if access is denied.
 */
export function RequireModule({
  module,
  children,
  fallback,
}: RequireModuleProps) {
  const { canAccess, isLoading, accessibleModules } = usePortalAccess();

  // While loading, don't render anything to avoid flash
  if (isLoading) {
    return null;
  }

  if (!canAccess(module)) {
    // If fallback is provided, use it
    if (fallback) {
      return <Navigate to={fallback} replace />;
    }

    // Find first accessible module to redirect to
    const firstAccessible = MODULE_PRIORITY.find((m) =>
      accessibleModules.has(m)
    );

    if (firstAccessible && firstAccessible !== module) {
      return <Navigate to={MODULE_ROUTES[firstAccessible]} replace />;
    }

    // If no accessible modules (shouldn't happen with valid roles), go to login
    return <Navigate to="/portal/login" replace />;
  }

  return <>{children}</>;
}
